import React, { FC, useEffect, useRef, useState } from "react";
import {
  PayPalScriptProvider,
  PayPalCardFieldsProvider,
  PayPalNameField,
  PayPalNumberField,
  PayPalExpiryField,
  PayPalCVVField,
  usePayPalCardFields,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import { Box, Button, CircularProgress } from "@mui/material";
import { PAYPAL_CLIENT_ID } from "@/config";
import { apiGetPaypalClientToken } from "@/features/profile/api/subscription.api";
import { Toastify } from "@/utils/toast";
import LoaderMini from "@/components/Loader-mini/LoaderMini";
import "./PaypalCardFields.styles.scss";

export type PaypalCardApproveData = {
  orderID: string;
  liabilityShift?: string;
};

interface PaypalCardFieldsProps {
  /** Crée l'ordre côté backend (mode CARD) et renvoie l'order id PayPal. */
  createOrder: () => Promise<string>;
  /**
   * Appelé après l'approbation (et le challenge 3DS éventuel). C'est ici que
   * l'appelant capture l'ordre via son endpoint dédié puis redirige/ferme.
   */
  onApprove: (data: PaypalCardApproveData) => Promise<void> | void;
  /** Notifie l'appelant d'une erreur (message déjà en français). */
  onError?: (message: string) => void;
  submitLabel?: string;
  currency?: string;
}

// Style appliqué à l'<input> RENDU DANS l'iframe PayPal (cross-origin → on ne
// peut le styler que via cette option SDK). On neutralise la bordure/ombre/padding
// internes pour ne garder qu'une seule bordure : celle de notre conteneur __field.
const PAYPAL_FIELD_STYLE = {
  input: {
    border: "none",
    "box-shadow": "none",
    outline: "none",
    padding: "0",
    margin: "0",
    // 16px : évite le zoom automatique des inputs au focus sur iOS/mobile.
    "font-size": "16px",
    "font-family":
      "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    color: "#333333",
  },
  ":focus": {
    "box-shadow": "none",
    outline: "none",
  },
  // Neutralise la bordure/ombre rouges que PayPal applique PAR DÉFAUT à l'input
  // invalide (dans l'iframe). L'état d'erreur est porté par notre conteneur
  // __field (.is-invalid) → une seule bordure rouge, alignée sur la boîte.
  // On ne garde ici que la couleur du texte en rouge.
  ".invalid": {
    border: "none",
    "box-shadow": "none",
    outline: "none",
    color: "#d32f2f",
  },
};

// Correspondance entre l'identifiant de champ émetteur (emittedBy) et la clé de
// son état dans PayPalCardFieldsStateObject.fields.
const FIELD_STATE_KEY: Record<string, string> = {
  name: "cardNameField",
  number: "cardNumberField",
  cvv: "cardCvvField",
  expiry: "cardExpiryField",
};

// Un champ est en erreur s'il a du contenu et n'est pas valide.
// - en cours de frappe (onChange) : on n'alerte que s'il n'est plus
//   "potentiellement valide" (saisie clairement erronée), pas pendant la frappe.
// - au blur : on alerte dès qu'il est invalide (incomplet inclus).
const isFieldInvalid = (field: any, onBlur: boolean): boolean => {
  if (!field || field.isEmpty || field.isValid) return false;
  return onBlur ? true : !field.isPotentiallyValid;
};

// Mapping des codes d'erreur de validation PayPal vers des messages français.
const CARD_ERROR_MESSAGES: Record<string, string> = {
  INVALID_NUMBER: "Numéro de carte invalide.",
  INVALID_EXPIRY: "Date d'expiration invalide.",
  INVALID_CVV: "Cryptogramme (CVV) invalide.",
  INVALID_NAME: "Nom du titulaire invalide.",
  INELIGIBLE_CARD_VENDOR: "Ce type de carte n'est pas accepté.",
};

/**
 * Bouton de soumission : doit être rendu À L'INTÉRIEUR du
 * PayPalCardFieldsProvider pour accéder au formulaire via usePayPalCardFields().
 */
const CardSubmitButton: FC<{
  label: string;
  submitting: boolean;
  setSubmitting: (value: boolean) => void;
  onError?: (message: string) => void;
  /** true dès que onApprove a été déclenché (carte autorisée par PayPal). */
  approvedRef: React.MutableRefObject<boolean>;
}> = ({ label, submitting, setSubmitting, onError, approvedRef }) => {
  const { cardFieldsForm } = usePayPalCardFields();
  const [{ isResolved }] = usePayPalScriptReducer();

  const handleClick = async () => {
    if (!cardFieldsForm) return;

    // Validation côté champs avant d'envoyer (numéro, expiration, CVV, nom).
    const state = await cardFieldsForm.getState();
    if (!state.isFormValid) {
      const message =
        state.errors && state.errors.length
          ? state.errors
              .map((code) => CARD_ERROR_MESSAGES[code] || code)
              .join(" ")
          : "Veuillez vérifier les informations de votre carte.";
      onError?.(message);
      Toastify(message, "error", 5000);
      return;
    }

    setSubmitting(true);
    try {
      // Déclenche la soumission + le challenge 3DS si nécessaire ; en cas de
      // succès, onApprove (provider) est appelé.
      await cardFieldsForm.submit();
    } catch (error) {
      setSubmitting(false);
      // Si onApprove a déjà été déclenché, c'est que PayPal a AUTORISÉ la carte :
      // un rejet de submit() ici est dû au démontage post-navigation (paiement
      // réussi), pas à un échec carte → on n'affiche aucune erreur.
      if (approvedRef.current) return;
      const message =
        "Le paiement par carte a échoué. Veuillez vérifier votre carte et réessayer.";
      onError?.(message);
      Toastify(message, "error", 5000);
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      fullWidth
      disabled={!isResolved || submitting}
      onClick={handleClick}
      sx={{ mt: 2, textTransform: "none", fontWeight: 600 }}
    >
      {submitting ? <CircularProgress size={22} color="inherit" /> : label}
    </Button>
  );
};

/**
 * Formulaire de paiement par carte intégré (PayPal Advanced Card / Card Fields).
 * Les champs sont rendus dans la page (iframes PayPal) : la carte ne transite
 * jamais par nos serveurs et l'utilisateur ne quitte pas le site.
 */
const PaypalCardFields: FC<PaypalCardFieldsProps> = ({
  createOrder,
  onApprove,
  onError,
  submitLabel = "Payer maintenant",
  currency = "EUR",
}) => {
  const [idToken, setIdToken] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Passe à true dès que PayPal appelle onApprove (carte autorisée). Sert à
  // distinguer un vrai échec carte d'un rejet de submit() dû au démontage.
  const approvedRef = useRef(false);

  // État d'erreur par champ (clé = emittedBy : name/number/cvv/expiry). Reflété
  // sur notre conteneur __field via la classe .is-invalid → bordure rouge unique
  // et alignée, dans le même esprit que :focus-within.
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  // Met à jour l'erreur du champ émetteur à partir de l'état renvoyé par PayPal.
  const updateFieldError = (data: any, onBlur: boolean) => {
    const key = data?.emittedBy;
    if (!key) return;
    const invalid = isFieldInvalid(data.fields?.[FIELD_STATE_KEY[key]], onBlur);
    setFieldErrors((prev) =>
      prev[key] === invalid ? prev : { ...prev, [key]: invalid }
    );
  };

  // Événements appliqués à chaque champ via le provider (cf. doc react-paypal-js).
  const inputEvents = {
    onChange: (data: any) => updateFieldError(data, false),
    onBlur: (data: any) => updateFieldError(data, true),
    // Au focus, on efface l'erreur du champ : l'utilisateur revient le corriger.
    onFocus: (data: any) => {
      const key = data?.emittedBy;
      if (key) {
        setFieldErrors((prev) =>
          prev[key] ? { ...prev, [key]: false } : prev
        );
      }
    },
  };

  useEffect(() => {
    let mounted = true;
    apiGetPaypalClientToken()
      .then((res: any) => {
        if (mounted) setIdToken(res?.data?.idToken ?? null);
      })
      .catch(() => {
        // On tente quand même le rendu avec le seul client-id (paiement unique).
        if (mounted) setIdToken(null);
      })
      .finally(() => {
        if (mounted) setTokenLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (tokenLoading) {
    return <LoaderMini />;
  }

  if (!PAYPAL_CLIENT_ID) {
    return (
      <Box sx={{ color: "error.main", textAlign: "center", py: 2 }}>
        Le paiement par carte est momentanément indisponible.
      </Box>
    );
  }

  const options: Record<string, unknown> = {
    clientId: PAYPAL_CLIENT_ID,
    components: "card-fields",
    currency,
    intent: "capture",
    locale: "fr_FR",
  };
  if (idToken) {
    options.dataUserIdToken = idToken;
  }

  const handleApprove = async (data: PaypalCardApproveData) => {
    // PayPal n'appelle onApprove que si la carte a été autorisée → on le note
    // tout de suite pour neutraliser un éventuel rejet tardif de submit().
    approvedRef.current = true;
    try {
      await onApprove(data);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "La validation du paiement a échoué. Veuillez réessayer.";
      onError?.(message);
      Toastify(message, "error", 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = () => {
    setSubmitting(false);
    const message = "Une erreur est survenue avec le paiement par carte.";
    onError?.(message);
    Toastify(message, "error", 5000);
  };

  return (
    <PayPalScriptProvider options={options as any}>
      <Box className="paypal-card-fields">
        <PayPalCardFieldsProvider
          createOrder={createOrder}
          onApprove={handleApprove}
          onError={handleError}
          style={PAYPAL_FIELD_STYLE as any}
          inputEvents={inputEvents}
        >
          <div className="paypal-card-fields__group">
            <label className="paypal-card-fields__label">
              Titulaire de la carte
            </label>
            <div
              className={`paypal-card-fields__field${
                fieldErrors.name ? " is-invalid" : ""
              }`}
            >
              <PayPalNameField placeholder="Nom du titulaire (optionnel)" />
            </div>
          </div>

          <div className="paypal-card-fields__group">
            <label className="paypal-card-fields__label">Numéro de carte</label>
            <div
              className={`paypal-card-fields__field${
                fieldErrors.number ? " is-invalid" : ""
              }`}
            >
              <PayPalNumberField placeholder="Numéro de carte" />
            </div>
          </div>

          <div className="paypal-card-fields__row">
            <div className="paypal-card-fields__group">
              <label className="paypal-card-fields__label">Expiration</label>
              <div
                className={`paypal-card-fields__field${
                  fieldErrors.expiry ? " is-invalid" : ""
                }`}
              >
                <PayPalExpiryField placeholder="MM / AA" />
              </div>
            </div>
            <div className="paypal-card-fields__group">
              <label className="paypal-card-fields__label">CVV</label>
              <div
                className={`paypal-card-fields__field${
                  fieldErrors.cvv ? " is-invalid" : ""
                }`}
              >
                <PayPalCVVField placeholder="CVV" />
              </div>
            </div>
          </div>

          <CardSubmitButton
            label={submitLabel}
            submitting={submitting}
            setSubmitting={setSubmitting}
            onError={onError}
            approvedRef={approvedRef}
          />
        </PayPalCardFieldsProvider>
      </Box>
    </PayPalScriptProvider>
  );
};

export default PaypalCardFields;
